import { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";
import { COLORS } from "../../constants/colors";

// Reusable filter section component
const FilterSection = ({ title, options, selectedFilters = [], onChange }) => (
  <div className="mb-4">
    <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
    <div className="space-y-2">
      {options.map((option) => (
        <label key={option.value} className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={selectedFilters.includes(option.value)}
            onChange={() => onChange(option.value)}
            className="w-4 h-4 border-gray-300 rounded cursor-pointer"
            style={{ accentColor: COLORS.purple }}
          />
          <span className="ml-2 text-sm text-gray-700">{option.label}</span>
        </label>
      ))}
    </div>
  </div>
);

// Reusable step component for recommended path
const PathStep = ({ number, title, description }) => (
  <div className="border border-gray-200 rounded-lg p-3">
    <div className="flex items-center space-x-2">
      <div className="w-6 h-6 text-white rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: COLORS.purple }}>
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
        <p className="text-xs text-gray-600">{description}</p>
      </div>
    </div>
  </div>
);

const FilterSidebar = ({ onFiltersChange }) => {
  const [filters, setFilters] = useState({
    difficulty: [],
    duration: [],
    price: []
  });

  const filterOptions = {
    difficulty: [
      { value: "beginner", label: "Beginner" },
      { value: "intermediate", label: "Intermediate" },
      { value: "advanced", label: "Advanced" }
    ],
    duration: [
      { value: "1-4", label: "1-4 Weeks" },
      { value: "5-8", label: "5-8 Weeks" },
      { value: "9-12", label: "9-12 Weeks" },
      { value: "13+", label: "13+ Weeks" }
    ],
    price: [
      { value: "free", label: "Free" },
      { value: "under50", label: "Under $50" },
      { value: "50-200", label: "$50-$200" },
      { value: "200+", label: "$200+" }
    ]
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (newFilters[filterType].includes(value)) {
        newFilters[filterType] = newFilters[filterType].filter(f => f !== value);
      } else {
        newFilters[filterType] = [...newFilters[filterType], value];
      }
      return newFilters;
    });
  };

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  const pathSteps = [
    { number: 1, title: "AI Fundamentals", description: "Build foundation knowledge" },
    { number: 2, title: "Tool Mastery", description: "Learn practical AI tools" },
    { number: 3, title: "Industry Application", description: "Apply to your field" }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filter Courses Section */}
      <div className="bg-white border-2 border-gray-300 rounded-lg shadow-lg p-3 sm:p-4">
        <h3 className="text-base font-bold text-gray-900 mb-3">Filter Courses</h3>
        
        <FilterSection 
          title="Difficulty Level" 
          options={filterOptions.difficulty}
          selectedFilters={filters.difficulty}
          onChange={(value) => handleFilterChange("difficulty", value)}
        />
        <FilterSection 
          title="Duration (Weeks)" 
          options={filterOptions.duration}
          selectedFilters={filters.duration}
          onChange={(value) => handleFilterChange("duration", value)}
        />
        <FilterSection 
          title="Price" 
          options={filterOptions.price}
          selectedFilters={filters.price}
          onChange={(value) => handleFilterChange("price", value)}
        />
      </div>

      {/* Recommended Path Section */}
      <div className="bg-white border-2 border-gray-300 rounded-lg shadow-lg p-3 sm:p-4">
        <div className="flex items-center space-x-2 mb-3">
          <BookOpen className="w-4 h-4" style={{ color: COLORS.purple }} />
          <h3 className="text-base font-bold text-gray-900">Recommended Path</h3>
        </div>
        
        <p className="text-gray-600 text-sm mb-4">
          Based on your career goals, we recommend starting with:
        </p>

        <div className="space-y-2">
          {pathSteps.map((step) => (
            <PathStep key={step.number} {...step} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar; 